// dashboard/src/pages/BillingPage.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import DualCreditBalanceCard from '../components/credits/DualCreditBalanceCard';
import SubscriptionPlansCard from '../components/credits/SubscriptionPlansCard';
import AddCustomCreditsForm from '../components/credits/AddCustomCreditsForm';
import TransactionHistory from '../components/credits/TransactionHistory';
import WelcomeToPlansCard from '../components/billing/WelcomeToPlansCard';
import { getCreditBalance } from '../services/creditService';

const BillingPage: React.FC = () => {
  // Get user's plan status to determine default tab
  const { data: creditBalance } = useQuery({
    queryKey: ['creditBalance'],
    queryFn: getCreditBalance,
  });

  const hasActivePlan = creditBalance?.plan_credits?.current_plan && 
                       creditBalance.plan_credits.current_plan !== 'free';

  // Default to plans tab if no active plan, otherwise overview
  const [activeTab, setActiveTab] = useState<'overview' | 'plans' | 'custom' | 'transactions'>(
    hasActivePlan ? 'overview' : 'plans'
  );
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Billing & Plans</h1>
      
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`${
                activeTab === 'overview'
                  ? 'border-success-500 text-success-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
            
            <button
              onClick={() => setActiveTab('plans')}
              className={`${
                activeTab === 'plans'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Plans
            </button>

            <button
              onClick={() => setActiveTab('custom')}
              className={`${
                activeTab === 'custom'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Custom Credits
            </button>

            <button
              onClick={() => setActiveTab('transactions')}
              className={`${
                activeTab === 'transactions'
                  ? 'border-success-500 text-success-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Transactions
            </button>
          </nav>
        </div>
      </div>
      
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Credit Balance Overview */}
          <DualCreditBalanceCard />

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setActiveTab('plans')}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  View Subscription Plans
                </button>
                <button
                  onClick={() => setActiveTab('custom')}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Buy Custom Credits
                </button>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  View Transaction History
                </button>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Credit System Info</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start">
                  <span className="text-blue-500 mr-2 mt-0.5">📅</span>
                  <div>
                    <p className="font-medium text-gray-900">Plan Credits</p>
                    <p>Monthly allocation that expires at cycle end</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5">♾️</span>
                  <div>
                    <p className="font-medium text-gray-900">Custom Credits</p>
                    <p>Never expire, used after plan credits</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-purple-500 mr-2 mt-0.5">⚡</span>
                  <div>
                    <p className="font-medium text-gray-900">Smart Usage</p>
                    <p>Plan credits consumed first automatically</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <TransactionHistory limit={5} />
        </div>
      )}

      {activeTab === 'plans' && (
        <div>
          {!hasActivePlan && <WelcomeToPlansCard />}
          <SubscriptionPlansCard />
        </div>
      )}

      {activeTab === 'custom' && (
        <AddCustomCreditsForm />
      )}

      {activeTab === 'transactions' && (
        <TransactionHistory />
      )}
    </div>
  );
};

export default BillingPage;
